import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  useDataProvider,
  useGetIdentity,
  useNotify,
  useTranslate,
} from "ra-core";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

import type { CrmDataProvider } from "../providers/types";
import type { Sale } from "../types";

/**
 * Almikia Epic EL3 (TASK-085): the `can_reassign_protected_leads` switch. Rendered ONLY for
 * direccion and never on her own profile (the server refuses both anyway). It saves at once
 * through `setCanReassignProtectedLeads` (a SECURITY DEFINER RPC), not through the user form,
 * because the generic user edit path can never change this column.
 */
export const ReassignPermissionSwitch = ({ sale }: { sale: Sale }) => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const { identity } = useGetIdentity();
  const [checked, setChecked] = useState(
    sale.can_reassign_protected_leads === true,
  );
  useEffect(() => {
    setChecked(sale.can_reassign_protected_leads === true);
  }, [sale.can_reassign_protected_leads]);

  const { mutate, isPending } = useMutation({
    mutationFn: (value: boolean) =>
      dataProvider.setCanReassignProtectedLeads({
        salesId: sale.id,
        value,
      }),
    onSuccess: (value) => {
      setChecked(value);
      notify(
        value ? "crm.reassign.permission_on" : "crm.reassign.permission_off",
      );
    },
    onError: (error) => {
      notify(
        error instanceof Error && error.message
          ? error.message
          : "crm.reassign.permission_error",
        { type: "error" },
      );
    },
  });

  if (identity?.role !== "direccion" || identity.id === sale.id) return null;

  return (
    <Card data-testid="reassign-permission-card">
      <CardHeader>
        <CardTitle className="text-base">
          {translate("crm.reassign.permission_title")}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <div className="flex min-h-11 items-center gap-3">
          <Switch
            id="can-reassign-protected-leads"
            checked={checked}
            disabled={isPending}
            onCheckedChange={(value) => mutate(value)}
          />
          <Label htmlFor="can-reassign-protected-leads">
            {translate("crm.reassign.permission_label")}
          </Label>
        </div>
        <p className="text-sm text-muted-foreground">
          {translate("crm.reassign.permission_help")}
        </p>
      </CardContent>
    </Card>
  );
};
